import React from 'react'
import { Button, Checkbox, Skeleton } from '@/shared/ui'
import { Table, Tbody, Td, Th, Thead, Tr } from '@/shared/ui/Table'
import { useToast } from '@/shared/ui/Toast'
import type { FinanceCostCategory, FinanceFixedCost } from '../../services/financeCostsService'
import { deleteFinanceFixedCost } from '../../services/financeCostsService'
import { formatMoneyFromCents } from '../../utils/money'
import { FinanceFixedCostModal } from './FinanceFixedCostModal'

type Props = {
  categories: FinanceCostCategory[]
  items: FinanceFixedCost[]
  loading: boolean
  showAll: boolean
  onToggleShowAll: (v: boolean) => void
  onRefresh: () => Promise<void>
}

export function FinanceFixedCostsPanel({ categories, items, loading, showAll, onToggleShowAll, onRefresh }: Props) {
  const { add: toast } = useToast()
  const [open, setOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<FinanceFixedCost | null>(null)
  const [deletingId, setDeletingId] = React.useState<string | null>(null)

  const catById = React.useMemo(() => new Map(categories.map((c) => [c.id, c.name])), [categories])

  async function onDelete(id: string) {
    const row = items.find((i) => i.id === id)
    const ok = window.confirm(`Excluir o custo recorrente "${row?.name || '—'}"?`)
    if (!ok) return
    try {
      setDeletingId(id)
      await deleteFinanceFixedCost(id)
      toast({ title: 'Custo excluído', variant: 'success', soft: true })
      await onRefresh()
    } catch (e: any) {
      toast({ title: 'Falha ao excluir', description: e?.message || String(e), variant: 'error', soft: true })
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2 text-sm text-foreground/70">
          <label className="inline-flex items-center gap-2">
            <Checkbox checked={showAll} onChange={(e) => onToggleShowAll((e.target as HTMLInputElement).checked)} />
            Mostrar também inativos
          </label>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true) }}>Novo custo recorrente</Button>
      </div>

      <div className="rounded-2xl border border-slate-800/80 bg-slate-950/80 p-4">
        {loading ? (
          <Skeleton className="h-28 w-full" />
        ) : (
          <div className="overflow-auto">
            <Table>
              <Thead>
                <Tr>
                  <Th>Nome</Th>
                  <Th>Categoria</Th>
                  <Th className="text-right">Valor mensal</Th>
                  <Th>Início</Th>
                  <Th>Término</Th>
                  <Th className="text-right">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map((r) => (
                  <Tr key={r.id}>
                    <Td className="min-w-[240px] font-medium">
                      <div>{r.name}</div>
                      {r.notes ? <div className="text-xs text-foreground/70">{r.notes}</div> : null}
                    </Td>
                    <Td className="min-w-[160px]">{r.category_id ? (catById.get(r.category_id) || '-') : '-'}</Td>
                    <Td className="text-right whitespace-nowrap">{formatMoneyFromCents(r.amount_cents, r.currency)}</Td>
                    <Td className="whitespace-nowrap">{r.start_month}</Td>
                    <Td className="whitespace-nowrap">{r.end_month || '—'}</Td>
                    <Td className="text-right whitespace-nowrap">
                      <div className="inline-flex gap-2">
                        <Button className="h-8 px-3" variant="outline" onClick={() => { setEditing(r); setOpen(true) }}>Editar</Button>
                        <Button className="h-8 px-3" variant="outline" disabled={deletingId === r.id} onClick={() => onDelete(r.id)}>
                          {deletingId === r.id ? 'Excluindo...' : 'Excluir'}
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
                {!items.length && (
                  <Tr>
                    <Td colSpan={6} className="text-sm text-foreground/70">Nenhum custo recorrente.</Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </div>
        )}
      </div>

      <FinanceFixedCostModal
        open={open}
        onClose={() => setOpen(false)}
        onSaved={onRefresh}
        categories={categories}
        initial={editing}
      />
    </div>
  )
}

